<template>
  <div id="devices">
    <DialogContainer v-if="confirmExport">
      <div class="adm-dialog">
        <h3>Export Devices</h3>
        <p>Download the current filtered list as a CSV?</p>
        <div class="adm-dialog-actions">
          <button class="adm-btn-ghost" @click="confirmExport = false">Cancel</button>
          <button class="adm-btn-primary" @click="yes">Export</button>
        </div>
      </div>
    </DialogContainer>

    <DialogContainer v-if="exportToCsv">
      <div class="adm-dialog">
        <i class="fa fa-download adm-dialog-icon" />
        <p>Exporting devices data…</p>
        <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
        <button class="adm-btn-ghost" @click="toggleExportDialog">Cancel</button>
      </div>
    </DialogContainer>

    <div class="adm-toolbar">
      <input
        v-model="textInput"
        placeholder="Search by id, device id, or email…"
        class="adm-input search-input"
        aria-label="Search"
      />
      <div class="adm-toolbar-spacer" />
      <button
        v-if="devices.length > 0"
        type="button"
        class="adm-btn-primary"
        @click="exportBtn"
      >
        <i class="fa fa-download" /> Export
      </button>
    </div>

    <div v-if="isLoading" class="adm-empty">
      <img src="../assets/admin_loading.gif" alt="Loading" class="loader" />
      <div class="adm-empty-sub">Loading devices…</div>
    </div>

    <div v-else-if="devices.length > 0" class="adm-card">
      <b-table
        id="devices-table"
        :items="devices"
        :fields="fields"
        hover
        small
        responsive
        :per-page="perPage"
        :current-page="currentPage"
        class="adm-table"
      >
        <template #cell(device_id)="data">
          {{ data.item.device_address }}
        </template>
      </b-table>

      <div v-if="devices.length > perPage" class="adm-pagination">
        <b-pagination
          v-model="currentPage"
          :total-rows="devices.length"
          :per-page="perPage"
          first-number
          last-number
          aria-controls="devices-table"
        />
      </div>
    </div>

    <div class="adm-empty" v-else>
      <div class="adm-empty-icon"><i class="fa fa-microchip" /></div>
      <div class="adm-empty-title">No devices</div>
      <div class="adm-empty-sub">
        Devices appear here once one is paired through the mobile app.
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_BASE_URL } from "@/utils/apiBase";
import { customAxios } from "@/utils/customAxios";
import { getAuthToken } from "@/utils/authStorage";
import { saveBlobResponse } from "@/utils/download";
import debounce from "lodash/debounce";
import DialogContainer from "@/components/DialogContainer.vue";

export default {
  components: {
    DialogContainer,
  },
  data() {
    return {
      confirmExport: false,
      isLoading: true,
      perPage: 20,
      currentPage: 1,
      exportToCsv: false,
      textInput: "",
      fields: [
        { key: "id", label: "ID" },
        { key: "device_id", label: "Device ID" },
        { key: "email", label: "Email" },
      ],
      devices: [],
      cancelTokenSource: null,
    };
  },
  watch: {
    textInput: { handler: "debouncedFetchDevices", immediate: true },
  },
  methods: {
    yes() {
      this.confirmExport = false;
      this.performExport();
    },
    exportBtn() {
      this.confirmExport = true;
    },
    performExport() {
      this.exportToCsv = true;
      this.cancelTokenSource = axios.CancelToken.source();
      axios
        .get(`${API_BASE_URL}/export-devices`, {
          params: { filter: "", search: this.textInput },
          cancelToken: this.cancelTokenSource.token,
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          responseType: "blob",
        })
        .then((response) => {
          // AppDevicesController::exportDevices returns Excel::download(...,
          // 'app_devices.xlsx') — a real xlsx, same as the users export.
          // Prefers the server's Content-Disposition when exposed.
          saveBlobResponse(response, "app_devices.xlsx");
          this.exportToCsv = false;
        })
        .catch((error) => {
          console.error("Export failed", error);
        });
    },
    fetchDevices() {
      this.isLoading = true;
      customAxios
        .post("/devices", { filter: "", search: this.textInput })
        .then((response) => {
          this.devices = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.error("Failed to load devices", error);
          this.devices = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    debouncedFetchDevices: debounce(function () {
      this.fetchDevices();
    }, 300),
    toggleExportDialog() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel("Export canceled by user.");
      }
      this.exportToCsv = false;
    },
  },
};
</script>

<style scoped>
.search-input {
  flex: 1;
  min-width: 12rem;
  max-width: 24rem;
}
.adm-dialog {
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  max-width: 480px;
  text-align: center;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}
.adm-dialog h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2540;
}
.adm-dialog p {
  color: #5b6577;
  font-size: 0.9rem;
}
.adm-dialog-icon {
  font-size: 2.5rem;
  color: #2a345d;
  margin-bottom: 0.5rem;
}
.adm-dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1rem;
}
.loader {
  width: 40px;
  height: 40px;
  margin-bottom: 0.5rem;
}
</style>
